<template>
  <tile class="opc-pbx">
    <foldable v-model="isExpanded" :title="this.$t('billing.planform.title.zoomphone')">
      <div slot="head">
        <div class="opc-pbx-head">
          <img class="opc-pbx__logo" :src="ZoomPhoneIcon" :alt="$t('billing.planform.title.zoomphone')" />
          <div class="opc-pbx-head__logo">
            <h1 class="opc-pbx__plan--name">
              Zoom Phone {{ $t(`billing.pbx.regions.US`) }}
              <i class="zm-icon-success main-icon" v-if="!isExpanded" aria-hidden="true"></i>
            </h1>
            <zm-button class="opc-pbx__link" type="link" v-if="isExpanded" @click="showPlanDetailDialog">
              {{ $t('billing.plan_details') }}
            </zm-button>
            <div class="opc-pbx__desc" v-if="!isExpanded">
              <ul>
                <li v-if="selectedCallingPlan">
                  <span>{{selectedCallingPlan.cartInfo.selected_number}} {{$t('billing.pbx.local_number.unit')}}  •   {{$money(selectedCallingPlan.amount)}}{{localUnit}}</span>
                </li>
                <li v-if="selectedTFPlan">
                  <span>{{selectedTFPlan.cartInfo.selected_number}} {{$t('billing.pbx.tollFree.unit')}}  •   {{$money(selectedTFPlan.amount)}}{{tfUnit}}</span>
                </li>
                <li v-if="selectedBasePlan && selectedBasePlan.billing_cycle === 'payAsYouGo'">
                  {{ $t('billing.pbx.cart_summary.pay_you_go_calling_fee', [callingFeeName]) }}
                </li>
                <li v-if="selectedBasePlan && selectedBasePlan.billing_cycle !== 'payAsYouGo'">
                  {{ callingFeeName }}  •  {{ `${$money(selectedBasePlan.amount)}${$t(`billing.common.per_billing_cycle.${selectedBasePlan.billing_cycle}`)}` }}
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div class="opc-pbx__main">
        <zm-form>
          <!--How many numbers do you need.-->
          <zm-form-item>
            <!--Total numbers Row-->
            <div class="number-item" data-opt-id="how-many-numbers">
              <div class="form-row form-row-1">
                <p class="label-title-1">
                  {{ $t('billing.pbx.how_many_number_need') }}
                  <zm-tooltip
                    v-if="showNumberList"
                    slot="label-suffix"
                    v-model="numberTipsVisible"
                    placement="top"
                    aria-label="Learn more about numbers"
                    :content="$t('billing.pbx.number_selected_tips')"
                  >
                    <zm-button type="icon" icon="zm-icon-info-outline main-color-icon" ghost size="mini"></zm-button>
                  </zm-tooltip>
                </p>
                <InputNumberWithPopover
                  v-if="!splitStyle"
                  :label="`${getCategoryName(form.category)} Select the number of plans`"
                  v-model="form.localNumber"
                  :min="localMin"
                  :max="localMax"
                  @change="handleLocalNumberChange"
                  size="large"
                  controls-position="both"
                />
                <span v-else-if="isMobile"></span>
                <span v-else>{{ $t('billing.pbx.x_number_selected', [totalPhoneNumbers]) }}</span>
              </div>
            </div>
            <!--Error Message row-->
            <zm-alert v-if="numberTips" class="number-item" :type="numberTips.type">
              <div class="number-tips-info" ref="numberTipsRef">
                <i class="zm-icon-info-outline" v-if="numberTips.type === 'info'" />
                <i class="billing-icon-warning" v-if="numberTips.type === 'warning'" />
                <p v-html="numberTips.text"></p>
              </div>
            </zm-alert>
            <!--local row-->
            <div class="number-item" data-opt-id="local-numbers">
              <!--local numbers-->
              <div class="form-row" v-if="splitStyle">
                <p class="label-title-2">{{ $t('billing.pbx.local_numbers') }}</p>
                <InputNumberWithPopover
                  :label="$t('billing.pbx.local_numbers')"
                  v-model="form.localNumber"
                  :min="localMin"
                  :max="localMax"
                  @change="handleLocalNumberChange"
                  size="large"
                  controls-position="both"
                />
              </div>
              <!--local number list row-->
              <div class="form-row" v-if="showNumberList && localNumberState.pickedNumberList.length">
                <div role="group">
                  <zm-tag
                    class="number-tag"
                    v-for="(numberItem, idx) in localNumberState.pickedNumberList"
                    :key="numberItem.label"
                    :closable="localNumberState.pickedNumberList.length > localMin"
                    @close="onRemoveNumber('local', idx)"
                    :delete-text="getDeletePhoneNumberAriaLabel(numberItem.displayName)"
                  >
                    {{ numberItem.displayName }}
                  </zm-tag>
                </div>
              </div>
              <!--local number opration row-->
              <div class="form-row" v-if="localOprationConf">
                <zm-button
                  v-if="localOprationConf.type !== 'loading'"
                  :round="localOprationConf.round"
                  :type="localOprationConf.type"
                  @click="localOprationConf.onClick"
                  class="edit-numbers"
                >{{ localOprationConf.text }}</zm-button>
                <div v-else class="opration-loading" v-loading="true" spinner="opration-loading-icon"></div>
              </div>
            </div>
            <!--toll-free row-->
            <div class="number-item" v-if="form.tollFreeNumber" data-opt-id="toll-free-numbers">
              <!--toll-free numbers-->
              <div class="form-row">
                <div>
                  <p class="label-title-2">{{ $t('billing.pbx.toll_free_numbers') }}</p>
                  <p class="label-title-sub" v-if="tfPriceLineText">{{ tfPriceLineText }}</p>
                </div>
                <InputNumberWithPopover
                  :label="$t('billing.pbx.toll_free_numbers')"
                  v-model="form.tollFreeNumber"
                  @change="handleTollFreeNumberChange"
                  :max="tollFreeMax"
                  size="large"
                  controls-position="both"
                />
              </div>
              <!--toll-free number list-->
              <div class="form-row" v-if="showNumberList && tollFreeNumberState.pickedNumberList.length">
                <!--TODO styles ax-->
                <div role="group">
                  <zm-tag
                    class="number-tag"
                    v-for="(numberItem, idx) in tollFreeNumberState.pickedNumberList"
                    :key="numberItem.label"
                    closable
                    @close="onRemoveNumber('toll-free', idx)"
                    :delete-text="getDeletePhoneNumberAriaLabel(numberItem.displayName)"
                  >
                    {{ numberItem.displayName }}
                  </zm-tag>
                </div>
              </div>
              <!--toll-free opration row-->
              <div class="form-row" v-if="tollFreeOprationConf">
                <zm-button
                  v-if="tollFreeOprationConf.type !== 'loading'"
                  :round="tollFreeOprationConf.round"
                  :type="tollFreeOprationConf.type"
                  @click="tollFreeOprationConf.onClick"
                  class="edit-numbers"
                >{{ tollFreeOprationConf.text }}</zm-button>
                <div v-else class="opration-loading" v-loading="true" spinner="opration-loading-icon"></div>
              </div>
            </div>

            <!--porting your number row-->
            <div class="number-item" data-opt-id="porting-numbers">
              <!--porting numbers-->
              <div class="form-row" v-if="portNumberList.length">
                <p class="label-title-2">{{ $t('billing.pbx.ready_for_porting') }}</p>
                <InputNumberWithPopover
                  label="Ready for porting number"
                  :value="portNumberList.length"
                  :min="portMin"
                  :max="portMax"
                  @input="handlePortNumberInput"
                  size="large"
                  controls-position="both"
                  ref="portNumberInput"
                />
              </div>
              <!--porting number list-->
              <div class="form-row" v-if="portNumberList.length">
                <div role="group">
                  <zm-tag
                    class="number-tag"
                    v-for="(numberItem, idx) in portNumberList"
                    :key="numberItem"
                    :closable="portNumberList.length > portMin"
                    @close="onRemovePortNumberList(idx)"
                    :delete-text="getDeletePhoneNumberAriaLabel(numberItem, true)"
                  >
                    {{ numberItem | phoneFormat }}
                  </zm-tag>
                </div>
              </div>
              <!--porting oprations-->
              <div class="form-row">
                <p v-if="!portNumberList.length">{{ $t('billing.pbx.have_existing_number') }}  <zm-button type="link" class="opc-pbx__link" @click="handlePortNumbers('Validate numbers for porting')">{{ $t('billing.pbx.valid_for_porting') }}</zm-button></p>
                <p v-else>{{ $t('billing.pbx.port_number_desc') }} <zm-button type="link" class="opc-pbx__link" @click="handlePortNumbers('Validate more numbers for porting')">{{ $t('billing.pbx.valid_more_porting') }}</zm-button></p>
              </div>
            </div>
          </zm-form-item>
          <zm-form-item class="vertical-form-item" :label="this.$t('billing.select_calling_plan')">
            <zm-tooltip
              v-model="callingPlanTipsVisible"
              slot="label-suffix"
              popper-class="pbx-title-tips"
              aria-label="Learn more about 'Select your calling plan'"
              placement="top"
              tab-to-leave
            >
              <p v-html="$t('billing.pbx.calling_plan_tips')" slot="content"></p>
              <zm-button @click="showCallingPlanTips" type="icon" icon="zm-icon-info-outline main-color-icon" ghost size="mini"></zm-button>
            </zm-tooltip>
            <zm-radio-group
              class="opc-card-radio billing-cycle-radios"
              v-model="pbxCallingPlan"
              @change="handleCallingPlanChange">
              <zm-radio
                v-for="(_, type) in basePlansGroupByCallingPlans"
                :key="type"
                :label="type">
                <p class="opc-pbx__cycle-title first-capitalize">
                  {{ $t(`billing.pbx.sub_types.${type}`) }}
                </p>
              </zm-radio>
            </zm-radio-group>
          </zm-form-item>
          <zm-form-item class="vertical-form-item" :label="$t('billing.select_billing_cycle')">
            <zm-radio-group
              class="opc-card-radio billing-cycle-radios"
              :aria-label="$t('billing.pbx.subscription_billing_cycle')"
              v-model="pbxBillingCycle"
              @change="handleBillingCycleChange">
              <zm-radio
                v-for="(_, cycle) in basePlansGroupByBillingCycle"
                :key="cycle"
                :label="cycle">
                <p class="opc-pbx__cycle-title first-capitalize">
                  {{ $t(`billing.billing_cycle.${cycle}`) }}
                  <save-count v-if="cycle === BILLING_CYCLE.ANNUAL" :monthly-price="costMap.monthly" :annual-price="costMap.annual" />
                </p>
                <p class="opc-pbx__cycle-cost"><span class="opc-pbx__cycle-cost-price">{{ $money(costMap[cycle], {forceDiff: true}) }}</span> {{ cycle === 'monthly' ? $t('billing.monthly_cost') : $t('billing.annual_cost') }}<priceVATTip /></p>
              </zm-radio>
            </zm-radio-group>
          </zm-form-item>
          <zm-form-item class="vertical-form-item" :label="callingFeeLabel" v-if="currentBasePlans.length">
            <zm-tooltip
              v-model="callingFeeTipsVisible"
              slot="label-suffix"
              popper-class="pbx-title-tips"
              aria-label="Learn more about 'How will international fees be handled?'"
              placement="top"
              tab-to-leave
            >
              <p v-html="pbxRateTips" slot="content" class="opc-pbx__fee-tip"></p>
              <zm-button @click="showCallingFeeTips" type="icon" icon="zm-icon-info-outline main-color-icon" ghost size="mini"></zm-button>
            </zm-tooltip>
            <zm-radio-group
              class="opc-card-radio calling-fees-radios"
              :aria-label="callingFeeLabel"
              v-model="pbxBasePlanCode"
              @change="handleBasePlanChange">
              <zm-radio
                v-for="plan in currentBasePlans"
                :key="plan.plan_code"
                :label="plan.plan_code"
              >
                <p class="opc-pbx__fees-title">{{ getCallingFeesText(plan).title }}</p>
                <p class="opc-pbx__fees-cost">
                  <span class="opc-pbx__fees-cost-price">{{ $money(plan.amount, { forceDiff: true }) }}</span>{{ getCallingFeesText(plan).unit }}<priceVATTip />
                </p>
              </zm-radio>
            </zm-radio-group>
          </zm-form-item>
        </zm-form>
      </div>
    </foldable>
  </tile>
</template>

<script>
import Foldable from '@/components/foldable.vue';
import Tile from '@/components/tile.vue';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import ZoomPhoneIcon from '@/assets/plans/logo_pbx.svg';
import { computedPbxBasePlans, computedPbxCallingPlans, computedPbxPlans, PBX_SUB_TYPES } from '@/common/service/computers/plans/pbx';
import { mapGetters, mapMutations, mapState } from 'vuex';
import { BILLING_CYCLE, planTypes } from '@/common/constants';
import phoneNumberApi from '../mixin/phone-number-api';
import { localNumberStore, tollFreeNumberStore, phoneNumberStorage } from '@/plans/ZOOM_PHONE/store';
import { values, isEmpty, groupBy, isNumber } from 'lodash'
import { PBX_REGIONS, phoneNumberTypeMap } from '@/plans/ZOOM_PHONE/helper/constants';
import { ServiceConfirm } from '@/components/common/service-dialog/confirm';
import SelectPhoneNumberDialog from '@/plans/ZOOM_PHONE/component/dialog/phone-number-select';
import ValidatePortNumberDialog from '@/plans/ZOOM_PHONE/component/dialog/port-number-validate';
import PlanDetails from '@/plans/ZOOM_PHONE/component/dialog/plan-details.vue';
import FullComparisonDialog from '@/plans/ZOOM_PHONE/component/dialog/full-comparison-dialog.vue';
import { parsePortNumber, getZPCallingFeesText } from '@/plans/ZOOM_PHONE/helper/utils';
import plansMixin from '@/plans/mx/plan';
import { logger } from '@/utils/log';
import { getPbxContactSalesLink } from '@/utils/i18n-shortcuts';
import SaveCount from '@/components/save-count.vue';
import { getPlanUnitWithCycle, planUnitMap } from '@/common/product-unit';
import { safeJSONParse } from '@/utils';
import { addSymbol, separators, toThousands } from '@/utils/money';
const PLAN_PAY_AS_YOU_GO = '7201';
const PLAN_PREPAID = '7202';

export default {
  key: planTypes[planTypes.ZOOM_PHONE],
  data() {
    return {
      ZoomPhoneIcon,
      isExpanded: true,
      numberTipsVisible: false,
      callingFeeTipsVisible: false,
      callingPlanTipsVisible: false,
      form: {
        category: '',
        localNumber: 0,
        tollFreeNumber: 0,
        numberList: [],
      },
      portNumberList: [],
      numberListLoading: true,
      localNumberState: localNumberStore.state,
      tollFreeNumberState: tollFreeNumberStore.state,
      pbxContactSalesLink: getPbxContactSalesLink(),
      BILLING_CYCLE
    };
  },
  components: {
    Foldable,
    Tile,
    InputNumberWithPopover,
    SaveCount
  },
  filters: {
    phoneFormat: parsePortNumber,
  },
  created() {
    this.initPbxForm().then(() => this.numberListLoading = false);
    this.selectPhoneNumberDialog = new ServiceConfirm(SelectPhoneNumberDialog, this.$store);
    this.validatePortNumberDialog = new ServiceConfirm(ValidatePortNumberDialog, this.$store);
    this.planDetailsDialog = new ServiceConfirm(PlanDetails, this.$store);
    this.fullComparisonDialog = new ServiceConfirm(FullComparisonDialog, this.$store);
    this.trackingContactSalesThis = this.trackingContactSales.bind(this);
  },
  mixins: [phoneNumberApi, plansMixin],
  computed: {
    ...mapState({
      planMapFlat: (state) => state.ShoppingCart.planMapFlat,
    }),
    ...mapGetters({
      'getSelectedPlans': 'getSelectedPlans',
      pbxBaseInfo: 'pbxBaseInfo',
      pbxCallingPlanType: 'pbxCallingPlanType',
    }),
    pbxBillingCycle: {
      get() {
        return this.pbxBaseInfo.billingCycle;
      },
      set(newValue) {
        this.setPbxBillingCycle(newValue);
      }
    },
    pbxCallingPlan: {
      get(){
        return this.pbxCallingPlanType;
      },
      set(newValue) {
        this.setPbxCallingPlan(newValue);
      }

    },
    pbxBasePlanCode: {
      get() {
        return this.pbxBaseInfo.basePlanCode;
      },
      set(newValue) {
        this.setPbxBasePlanCode(newValue);
      }
    },
    basePlans() {
      return computedPbxBasePlans(this.planMapFlat);
    },
    callingPlans() {
      return computedPbxCallingPlans(this.planMapFlat);
    },
    basePlansGroupByCallingPlans() {
      const { UC, MC } = groupBy(this.callingPlans, 'sub_type');
      return{
        UC: [...UC],
        MC: [...MC]
      }
    },
    basePlansGroupByBillingCycle() {
      const { annual, monthly, payAsYouGo } = groupBy(this.basePlans, 'billing_cycle');
      return {
        monthly: [...payAsYouGo, ...monthly],
        annual: [...payAsYouGo, ...annual],
      };
    },
    allPbxPlans() {
      return computedPbxPlans(this.planMapFlat);
    },
    selectedPbxPlans() {
      return computedPbxPlans(this.getSelectedPlans);
    },
    selectedBasePlan() {
      return computedPbxBasePlans(this.getSelectedPlans)[0];
    },
    selectedCallingPlan() {
      return computedPbxCallingPlans(this.getSelectedPlans)[0];
    },
    selectedTFPlan() {
      return computedPbxPlans(this.getSelectedPlans).find(item => item.sub_type === PBX_SUB_TYPES.TOLLFREE);
    },
    currentBasePlans() {
      return this.basePlansGroupByBillingCycle[this.pbxBillingCycle] || [];
    },
    defaultTFPlan() {
      if(this.selectedTFPlan) {
        return this.selectedTFPlan;
      }
      return this.allPbxPlans.find(item => (
        item.region === PBX_REGIONS.US_AND_CANADA &&
        item.billing_cycle === this.pbxBillingCycle &&
        item.sub_type === PBX_SUB_TYPES.TOLLFREE
      ));
    },
    costMap() {
      const selectedCategoryNumberMap = this.selectedPbxPlans
      .filter(item => (
        item.sub_type === PBX_SUB_TYPES.UNLIMITED ||
        item.sub_type === PBX_SUB_TYPES.METERED ||
        item.sub_type === PBX_SUB_TYPES.TOLLFREE
      ))
      .reduce((pre, item) => {
        pre[item.category] = item.cartInfo.selected_number;
        return pre;
      }, {});
      const monthlyPlans = this.allPbxPlans.filter(item => selectedCategoryNumberMap[item.category] && item.billing_cycle === BILLING_CYCLE.MONTHLY);
      const annualPlans = this.allPbxPlans.filter(item => selectedCategoryNumberMap[item.category] && item.billing_cycle === BILLING_CYCLE.ANNUAL);
      const _costMap = {
        [BILLING_CYCLE.MONTHLY]: monthlyPlans.reduce((perviousValue, currentValue) => {
          return perviousValue + currentValue.amount * selectedCategoryNumberMap[currentValue.category];
        }, 0),
        [BILLING_CYCLE.ANNUAL]: annualPlans.reduce((perviousValue, currentValue) => {
          return perviousValue + currentValue.amount * selectedCategoryNumberMap[currentValue.category];
        }, 0),
      };
      return _costMap;
    },
    totalPhoneNumbers() {
      return this.form.localNumber + this.form.tollFreeNumber + this.portNumberList.length;
    },
    localMin() {
      if(this.portNumberList.length > 0) {
        return 0;
      }
      return 1;
    },
    localMax() {
      return 99 - (this.totalPhoneNumbers - this.form.localNumber);
    },
    tollFreeMax() {
      return 99 - (this.totalPhoneNumbers - this.form.tollFreeNumber);
    },
    portMin() {
      if(this.form.localNumber > 0) {
        return 0;
      }
      return 1;
    },
    portMax() {
      const maxLen = 99 - (this.totalPhoneNumbers - this.portNumberList.length);
      return Math.min(maxLen, 10);
    },
    canSelectedLocalNumber() {
      return this.localNumberState.searchTimes > 0;
    },
    canSelectedTollFreeNumber() {
      return this.tollFreeNumberState.searchTimes > 0;
    },
    canPopulateLocalNumber() {
      return (
        this.totalPhoneNumbers <= 10 &&
        this.localNumberState.phoneNumberList.length > this.form.localNumber
      );
    },
    canPopulateTollFreeNumber() {
      return (
        this.totalPhoneNumbers <= 10 &&
        this.tollFreeNumberState.phoneNumberList.length > this.form.tollFreeNumber
      );
    },
    splitStyle() {
      return (
        this.portNumberList.length ||
        this.form.tollFreeNumber ||
        (!this.tollFreeNumberState.phoneNumberList.length && this.tollFreeNumberState.searchTimes > 0)
      );
    },
    showLocalNumberSelectButton() {
      return this.showNumberList && (!this.localNumberState.phoneNumberList.length && this.localNumberState.searchTimes <= 0);
    },
    showNumberList() {
      if(this.portNumberList.length >= 10) {
        return false;
      }
      return this.totalPhoneNumbers <= 10;
    },
    showLocalNumberEditButton() {
      return (this.localNumberState.phoneNumberList.length || this.localNumberState?.pickedNumberList.length)  && this.showNumberList;
    },
    showTollFreeNumberSelectButton() {
      return this.showNumberList && (!this.tollFreeNumberState.phoneNumberList.length && this.tollFreeNumberState.searchTimes <= 0)
    },
    showTollFreeNumberEditButton() {
      return (this.tollFreeNumberState.phoneNumberList.length || this.tollFreeNumberState?.pickedNumberList.length) && this.showNumberList;
    },
    tfPriceLineText() {
      const defaultTFPlan = this.defaultTFPlan;
      if(!defaultTFPlan) {
        return '';
      }
      const unit = this.$t(`billing.common.per_billing_cycle.${defaultTFPlan.billing_cycle}`);
      const price = defaultTFPlan.amount * defaultTFPlan.cartInfo.selected_number;
      return this.$t('billing.pbx.tf_pricing_line', [`${this.$money(price)}${unit}`])
    },
    numberTips() {
      if (this.totalPhoneNumbers >= 99) {
        return {
          text: this.$t('billing.pbx.more_than_99_license', [this.pbxContactSalesLink]),
          type: 'warning',
        };
      } else if (
        (!this.localNumberState.phoneNumberList.length && this.localNumberState.searchTimes > 0)
      ) {
        return {
          text: this.$t('billing.pbx.number_tips_1'),
          type: 'info',
        };
      } else if (!this.showNumberList) {
        return {
          type: 'info',
          text: this.$t('billing.pbx.number_tips_2'),
        }
      }
      return null;
    },
    localUnit() {
      if(this.selectedCallingPlan) {
        return getPlanUnitWithCycle(this.selectedCallingPlan.billing_cycle, planUnitMap.number);
      }
      return '';
    },
    tfUnit() {
      if(this.selectedTFPlan) {
        return getPlanUnitWithCycle(this.selectedTFPlan.billing_cycle, planUnitMap.number);
      }
      return '';
    },
    callingFeeLabel() {
      if(!this.selectedCallingPlan) return this.$t('billing.pbx.calling_fee_label.unlimited');
      if(this.selectedCallingPlan.sub_type === PBX_SUB_TYPES.UNLIMITED) return this.$t('billing.pbx.calling_fee_label.unlimited');
      if(this.selectedCallingPlan.sub_type === PBX_SUB_TYPES.METERED) return this.$t('billing.pbx.calling_fee_label.metered');
      return this.$t('billing.pbx.calling_fee_label.unlimited');
    },
    callingFeeName() {
      if(!this.selectedBasePlan) return '';
      return this.$t('billing.pbx.metered_calling_credits');
    },
    localOprationConf() {
      if(this.numberListLoading) {
        return {
          type: 'loading',
        };
      } else if(this.showLocalNumberSelectButton) {
        return {
          type: 'primary',
          onClick: () => this.handleEditNumbers('local'),
          text: this.$t('billing.pbx.select_your_numbers'),
          round: true,
        };
      } else if(this.showLocalNumberEditButton) {
        return {
          type: 'link',
          onClick: () => this.handleEditNumbers('local'),
          text: this.$t('billing.pbx.edit_numbers'),
          round: false,
        };
      } else if(!this.form.tollFreeNumber && this.totalPhoneNumbers < 99) {
        return {
          type: 'link',
          onClick: () => this.addATollFreeNumber(),
          text: this.$t('billing.pbx.add_toll_free_numbers'),
          round: false,
        };
      }
      return null;
    },
    tollFreeOprationConf() {
      if(this.numberListLoading) {
        return {
          type: 'loading',
        };
      } else if(this.showTollFreeNumberSelectButton) {
        return {
          type: 'primary',
          onClick: () => this.handleEditNumbers(phoneNumberTypeMap.local),
          text: this.$t('billing.pbx.select_your_numbers'),
          round: true,
        };
      } else if(this.showTollFreeNumberEditButton) {
        return {
          type: 'link',
          onClick: () => this.handleEditNumbers(phoneNumberTypeMap.tollFree),
          text: this.$t('billing.pbx.edit_numbers'),
          round: false,
        };
      }
      return null;
    },
    isMobile(){
      return this.$context.is.mobile || this.$context.is.tablet
    },
    pbxRates(){
      const pbxRateList = safeJSONParse(this.$config('pbxDefaultRateForUs')) || [];
      const rates = [];
      if (!Array.isArray(pbxRateList)) return []
      pbxRateList.forEach(item => {
        if (item?.plan === PLAN_PAY_AS_YOU_GO || item?.plan === PLAN_PREPAID) {
          rates[item.plan] = item.rate;
        }
      });
      return rates;
    },
    pbxRateTips() {
      const { [PLAN_PAY_AS_YOU_GO]: payAsYouGo, [PLAN_PREPAID]: prepaid } = this.pbxRates; 
      const isMeteredSelected = this.selectedCallingPlan.sub_type === PBX_SUB_TYPES.METERED
      if (payAsYouGo && prepaid && isMeteredSelected) {
        const payAsYouGoRate = this.getRatePrice(payAsYouGo);
        const prepaidRate = this.getRatePrice(prepaid);
        if (payAsYouGoRate === 0 || prepaidRate === 0) {
          return this.$t('billing.pbx.calling_fee_tips', [this.$t('billing.pbx.regions.US')]);
        }
        return this.$t('billing.pbx.calling_fee_tips_new', [payAsYouGoRate, prepaidRate]);
      }
      return this.$t('billing.pbx.calling_fee_tips', [this.$t('billing.pbx.regions.US')]);
    }
  },
  methods: {
    getDeletePhoneNumberAriaLabel(number, shouldFormat = false) {
      const removeTxt = this.$t('billing.common.remove');
      const displayName = shouldFormat ? parsePortNumber(number) : number;
      return `${removeTxt} ${displayName}`;
    },
    getPlanUnitWithCycle,
    ...mapMutations({
      updateSelectPlan: 'updateSelectPlan',
      setPbxBasePlanCode: 'setPbxBasePlanCode',
      setPbxBillingCycle: 'setPbxBillingCycle',
      setPbxCallingPlan: 'setPbxCallingPlan',
    }),
    getCallingFeesText: getZPCallingFeesText,
    updateCallingPlan(localNumber, portNumber) {
      const totalQuantity = localNumber + portNumber;

      this.selectedCallingPlan && totalQuantity > 0 && this.updateSelectPlan({
        plan_code: this.selectedCallingPlan.plan_code,
        cartInfo: { selected_number: totalQuantity },
      });
    },
    updateTFPlan(tollFreeNumber) {
      this.defaultTFPlan && this.updateSelectPlan({
        plan_code: this.defaultTFPlan.plan_code,
        cartInfo: { selected_number: tollFreeNumber },
      });
    },

    getCategoryName(category) {
      if(!category) {
        return '';
      }
      return this.$t(`billing.pbx.plan_name.${category}`);
    },

    async initPbxForm() {
      if(this.selectedBasePlan) {
        this.pbxBasePlanCode = this.selectedBasePlan.plan_code;
      }
      if(this.selectedCallingPlan) {
        this.pbxBillingCycle = this.selectedCallingPlan.billing_cycle;
        this.form.category = this.selectedCallingPlan.category;
        this.form.localNumber = Number(this.selectedCallingPlan.cartInfo.selected_number) - this.portNumberList.length;
        this.pbxCallingPlan = this.selectedCallingPlan.sub_type;
      } else if(!this.selectedBasePlan.is_payg) {
        this.pbxBillingCycle = this.selectedBasePlan.billing_cycle;
      } else {
        this.pbxBillingCycle = BILLING_CYCLE.ANNUAL;
      }

      if(this.selectedTFPlan) {
        this.form.tollFreeNumber = Number(this.selectedTFPlan.cartInfo.selected_number);
      }

      const phoneNumberCache = phoneNumberStorage.getState();
      if(isEmpty(phoneNumberCache)) {
        await this.fetchDefaultPhoneNumber();
        if (this._isDestroyed) {
          return;
        }
        this.selectedCallingPlan && this.populateNumber(phoneNumberTypeMap.local, this.form.localNumber);
        this.selectedTFPlan && this.populateNumber(phoneNumberTypeMap.tollFree, this.form.tollFreeNumber);
      } else {
        this.localNumberState = Object.assign(this.localNumberState, phoneNumberCache.localNumberState || {});
        this.tollFreeNumberState = Object.assign(this.tollFreeNumberState, phoneNumberCache.tollFreeNumberState || {});
        this.portNumberList = phoneNumberCache.portNumberList || [];
        this.form.localNumber = Number(this.selectedCallingPlan.cartInfo.selected_number) - this.portNumberList.length;
      }
    },
    async fetchDefaultPhoneNumber() {
      const [localNumberList, tollFreeNumberList = []] = await Promise.all([
        this.searchLocalNumbers(this.getLocalNumberParamsWithIp()),
        this.searchTollFreeNumbers(this.getTollFreeNumberParamsWithIp()),
      ]);
      if (this._isDestroyed) {
        return;
      }
      this.localNumberState.phoneNumberList = localNumberList;
      this.localNumberState.cachePhoneNumberList = localNumberList;
      this.tollFreeNumberState.phoneNumberList = tollFreeNumberList;
      this.tollFreeNumberState.cachePhoneNumberList = tollFreeNumberList;

      this.storagePhoneNumber();
    },
    handleLocalNumberChange(value, oldValue) {
      this.updateCallingPlan(value, this.portNumberList.length);
      const numberListLen = this.localNumberState.pickedNumberList.length;
      if (this.totalPhoneNumbers <= 10 && value > numberListLen) {
        this.populateNumber(phoneNumberTypeMap.local, value - numberListLen);
      } else if(numberListLen > value) {
        this.removeNumber(phoneNumberTypeMap.local, value);
      }
      const typeName = this.selectedCallingPlan?.sub_type === PBX_SUB_TYPES.METERED ? 'us & canada metered' : 'us & canada unlimited';
      const opType = value > oldValue ? 'increase' : 'decrease';
      this.$tracking.buyInteraction('zoom phone plan Local Numbers', `${typeName} - ${opType} - ${value}`);
    },
    handleTollFreeNumberChange(value, oldValue) {
      this.updateTFPlan(value);
      const numberListLen = this.tollFreeNumberState.pickedNumberList.length;
      if(this.totalPhoneNumbers <= 10 && numberListLen < value) {
        this.populateNumber(phoneNumberTypeMap.tollFree, value - numberListLen)
      } else if(numberListLen > value) {
        this.removeNumber(phoneNumberTypeMap.tollFree, value);
      }
      const opType = value > oldValue ? 'increase' : 'decrease';
      this.$tracking.buyInteraction('zoom phone plan Toll-free Numbers', `TollFree Numbers - ${opType} - ${value}`);
    },
    onRemoveNumber(type, idx) {
      let phoneNumerState = this.localNumberState;
      if(type === phoneNumberTypeMap.tollFree) {
        phoneNumerState = this.tollFreeNumberState;
      }
      phoneNumerState.pickedNumberList = phoneNumerState.pickedNumberList.filter((_, i) => idx !== i);
      if(type === phoneNumberTypeMap.local) {
        this.form.localNumber--;
        this.updateCallingPlan(this.form.localNumber, this.portNumberList.length);
      } else {
        this.form.tollFreeNumber--;
        this.updateTFPlan(this.form.tollFreeNumber);
      }
      this.storagePhoneNumber();
    },
    /**
     *
     * @param {local | toll-free} type
     * @param {number} license
     */
    populateNumber(type, license) {
      let phoneNumerState = this.localNumberState;
      if(type === phoneNumberTypeMap.tollFree) {
        phoneNumerState = this.tollFreeNumberState;
      }
      const res = Array.from({ length: license }, () => {
        const nextNumber = phoneNumerState.cachePhoneNumberList
        .find(item => !phoneNumerState.pickedNumberList.find(item2 => item2.label === item.label));
        nextNumber && phoneNumerState.pickedNumberList.push(nextNumber);
        return nextNumber;
      }).filter(Boolean);

      this.storagePhoneNumber();
      return res
    },
    removeNumber(type, value) {
      let phoneNumerState = this.localNumberState, arr = [];
      if(type === phoneNumberTypeMap.tollFree) {
        phoneNumerState = this.tollFreeNumberState;
      }
      arr = [...phoneNumerState.pickedNumberList];
      arr = arr.slice(0, value);
      phoneNumerState.pickedNumberList = arr;
      this.storagePhoneNumber();
    },
    handleEditNumbers(type) {
      this.$tracking.buyInteraction('zoom phone numbers', `${this.getCategoryName(this.form.category)} - edit numbers`);
      const checkedPhoneNumberMap = [
        ...this.localNumberState.pickedNumberList,
        ...this.tollFreeNumberState.pickedNumberList,
      ].reduce((pre, item) => ({
        ...pre,
        [item.label]: item,
      }), {})
      this.selectPhoneNumberDialog.confirm({
        clearable: !this.localMin,
        checkedPhoneNumberMap,
        searchByPhoneType: type,
        localCount: this.form.localNumber,
        tollFreeCount: this.form.tollFreeNumber,
        portCount: this.portNumberList.length,
      }).then(res => {
        this.syncNumberListToCart(res);
      });
    },
    syncNumberListToCart(res) {
      const checkedPhoneNumberList = values(res.checkedPhoneNumberMap);
      this.localNumberState.pickedNumberList = checkedPhoneNumberList.filter(item => item.type === phoneNumberTypeMap.local);
      this.tollFreeNumberState.pickedNumberList = checkedPhoneNumberList.filter(item => item.type === phoneNumberTypeMap.tollFree);
      if(this.localNumberState.phoneNumberList.length && this.localNumberState.pickedNumberList.length) {
        this.form.localNumber = this.localNumberState.pickedNumberList.length;
      }
      if(this.tollFreeNumberState.phoneNumberList.length) {
        this.form.tollFreeNumber = this.tollFreeNumberState.pickedNumberList.length;
      }

      this.updateCallingPlan(this.form.localNumber, this.portNumberList.length);
      this.updateTFPlan(this.form.tollFreeNumber);
      this.storagePhoneNumber();
    },
    addATollFreeNumber() {
      this.$tracking.buyInteraction('zoom phone numbers', `${this.getCategoryName(this.form.category)} - add toll-free lines`);
      this.form.tollFreeNumber = 1;
      this.populateNumber(phoneNumberTypeMap.tollFree, 1);
      this.updateTFPlan(1);
    },
    handlePortNumbers(source) {
      this.$tracking.buyInteraction('zoom phone numbers', `${this.getCategoryName(this.form.category)} - ${source}`);
      this.validatePortNumberDialog.confirm({
        checkedNumberList: [...this.portNumberList],
        originPortNumberList: [...this.portNumberList],
        clearable: !this.portMin,
        localCount: this.form.localNumber,
        tollFreeCount: this.form.tollFreeNumber,
        portCount: this.portNumberList.length,
      }).then((data) => {
        this.portNumberList = data;
        this.updateCallingPlan(this.form.localNumber, data.length);
        this.storagePhoneNumber();
      })
    },
    onRemovePortNumberList(idx) {
      this.portNumberList = this.portNumberList.filter((_, i) => idx !== i);
      this.updateCallingPlan(this.form.localNumber, this.portNumberList.length);
    },
    storagePhoneNumber() {
      phoneNumberStorage.store({
        localNumberState: this.localNumberState,
        tollFreeNumberState: this.tollFreeNumberState,
        portNumberList: this.portNumberList,
      });
    },
    handlePortNumberInput(value) {
      const oldValue = this.portNumberList.length;
      if(oldValue > value) {
        this.$tracking.buyInteraction('zoom phone plan numbers ready for porting', `${this.getCategoryName(this.form.category)} - decrease - ${value}`);
        this.portNumberList = this.portNumberList.slice(0, value);
        this.updateCallingPlan(this.form.localNumber, value);
        this.storagePhoneNumber();
      } else if(oldValue < value) {
        this.handlePortNumbers('increase number input');
        setTimeout(() => {
          try {
            this.$refs.portNumberInput._data.currentValue = oldValue
          } catch (error) {
            logger.error(error);
          }
        });
      }
    },
    handleCallingPlanChange(value){
      const selectedCategoryNumber = this.selectedPbxPlans
        .filter(item => item.sub_type === PBX_SUB_TYPES.UNLIMITED ||
        item.sub_type === PBX_SUB_TYPES.METERED
        )
        .reduce((pre, item) => {
          pre = item.cartInfo.selected_number;
          return pre;
        }, {});
        const selectedCallingPlans = this.callingPlans
          .filter(item => item.region === PBX_REGIONS.US_AND_CANADA 
          && item.billing_cycle === this.pbxBillingCycle)
        selectedCallingPlans
          .filter(item => item.sub_type !== value)
          // remove selected calling plan
          .forEach(item => this.updateSelectPlan({ plan_code: item.plan_code, cartInfo: { selected_number: 0 } }))
        selectedCallingPlans
          .filter(item => item.sub_type === value)
          .forEach(item => this.updateSelectPlan({ plan_code: item.plan_code, cartInfo: { selected_number:selectedCategoryNumber} }))
    },
    handleBillingCycleChange(value) {
      this.$tracking.buyInteraction('Zoom Phone subscription cycle', value);
      const selectedBasePlanCategoryNumberMap = this.selectedPbxPlans
      .filter(item => item.sub_type === PBX_SUB_TYPES.BASE)
      .reduce((pre, item) => {
        pre[item.category] = item.cartInfo.selected_number;
        return pre;
      }, {});
      const selectedCategoryNumberMap = this.selectedPbxPlans
      .filter(item => item.sub_type !== PBX_SUB_TYPES.BASE)
      .reduce((pre, item) => {
        pre[item.category] = item.cartInfo.selected_number;
        return pre;
      }, {});
      this.switchPlansBillingCycle(selectedCategoryNumberMap, value);
      if(this.selectedBasePlan.billing_cycle !== BILLING_CYCLE.PAY_AS_YOU_GO) {
        this.switchPlansBillingCycle(selectedBasePlanCategoryNumberMap, value);
        this.pbxBasePlanCode = this.selectedBasePlan.plan_code;
      }
    },
    switchPlansBillingCycle(categoryNumberMap, billingCycle) {
      this.allPbxPlans
        // get selected plans
        .filter(item => categoryNumberMap[item.category])
        // remove selected plans
        .forEach(item => this.updateSelectPlan({ plan_code: item.plan_code, cartInfo: { selected_number: 0 } }))
        // get plans by billing cycle

      this.allPbxPlans
        .filter(item => categoryNumberMap[item.category] && item.billing_cycle === billingCycle)
        // add plan to cart
        .forEach(item => {
          if(categoryNumberMap[item.category]) {
            this.updateSelectPlan({ plan_code: item.plan_code, cartInfo: { selected_number: categoryNumberMap[item.category] } })
            categoryNumberMap[item.category] = 0;
          }
        });
    },
    handleBasePlanChange(value) {

      this.updateSelectPlan({
        plan_code: this.selectedBasePlan.plan_code,
        cartInfo: { selected_number: 0 },
      });
      this.updateSelectPlan({
        plan_code: value,
        cartInfo: { selected_number: 1 },
      });
      this.$nextTick(() => {
        const cycle = this.selectedBasePlan?.billing_cycle === 'monthly' ? 'month' : 'year'
        const baseName = this.selectedBasePlan.billing_cycle !== BILLING_CYCLE.PAY_AS_YOU_GO ?
        `prepaid ${this.$money(this.selectedBasePlan.amount)}/${cycle}` :
        `Pay as You Go'`;
        this.$tracking.buyInteraction('zoom phone international calling fees', baseName);
      })
    },

    async showPlanDetailDialog() {
      this.$tracking.buyInteraction('plan details selected', 'zoom phone');
      await this.planDetailsDialog.confirm({ subType: this.selectedCallingPlan.sub_type })
      await this.fullComparisonDialog.confirm({ subType: this.selectedCallingPlan.sub_type });
      if(this.selectedCallingPlan.sub_type === PBX_SUB_TYPES.METERED) {
        this.$emit('remove', planTypes[planTypes.ZOOM_PHONE$ZPI]);
      }
      this.form.category = this.selectedCallingPlan.category;
    },
    trackingContactSales(e) {
      if(e.target.href) {
        this.$tracking.buyInteraction('zoom phone numbers', `zoom phone - need more than 99 lines - contact sales`);
      }
    },
    showCallingFeeTips(){
      if(this.$context.is.mobile){
        this.callingFeeTipsVisible = !this.callingFeeTipsVisible
      }
    },
    showCallingPlanTips(){
      if(this.$context.is.mobile){
        this.callingPlanTipsVisible = !this.callingPlanTipsVisible
      }
    },
    getRatePrice(ratePrice = 0) {
      if(!isNumber(ratePrice)){
        return 0
      }
      const price = +parseFloat(ratePrice / 100)?.toFixed(5);
      const { commaStyle, decimal } = separators();
      let [integerPart, decimalPart] = price.toFixed(5).split('.');
      integerPart = toThousands(integerPart, commaStyle);
      decimalPart = decimalPart ? `${decimal}${parseFloat(`0.${decimalPart}`).toString().slice(2)}` : '';

      const formattedPrice = integerPart + decimalPart;

      return addSymbol(formattedPrice);
    },
  },
  watch: {
    numberTips(v) {
      if(v) {
        this.$nextTick(() => {
          if(this.$refs.numberTipsRef) {
            this.$refs.numberTipsRef.removeEventListener('click', this.trackingContactSalesThis);
            this.$refs.numberTipsRef.addEventListener('click', this.trackingContactSalesThis);
          }
        });
      }
    },
    numberTipsVisible(v) {
      v && this.$tracking.buyInteraction('zoom phone options', 'How many numbers do you need? - tooltip');
    },
    callingFeeTipsVisible(v) {
      v && this.$tracking.buyInteraction('zoom phone options', 'How will international fees be handled? - tooltip');
    },
    selectedTFPlan(value) {
      if(!value) {
        this.form.tollFreeNumber = 0;
        this.tollFreeNumberState.pickedNumberList = [];
      }
    },
    showNumberList(value, oldValue) {
      if(value === oldValue) return false;
      this.localNumberState.showNumberList = value;
      this.tollFreeNumberState.showNumberList = value;
      this.storagePhoneNumber();
    },
    isExpanded: {
      handler(val) {
        this.$tracking.buyInteraction(
          'product details accordion',
          `zoom phone - ${val ? 'expand' : 'collapse'}`,
        );
      },
    },
  },
};
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
@import '@zoom/billing-responsive/styles/responsive.scss';

.opc-pbx {

  .pbx-title-tips{
    @include media(mobile,tablet){
      min-width: 250px;
    }
  }
  &__fee-tip{
    min-width: 250px;
  }
  .main-icon {
    color: $theme-color;
  }
  .zm-button--link {
    color: $theme-color;
  }
  &__plan--name {
    font-style: normal;
    font-weight: 600;
    font-size: 20px;
    font-family: $happy-display;
    color: #00053D;
    margin-bottom: 5px;
    display: flex;
    flex-direction: row;
    align-items: center;
    .zm-icon-success {
      font-size: 16px;
      margin-left: 12px;
    }
    @include media(mobile,tablet){
      padding-right: 60px;
      display: block;

      .main-icon.zm-icon-success {
        margin-left: 5px;
      }
    }
  }

  .zm-collapse-item.is-active{
    .opc-pbx__name {

    }
  }
  &__link {
    padding: 0;
    margin: 0;
    color: $theme-color;
    font-weight: 600 !important;
  }
  &__main {
    margin-top: 24px;
    border-top: 1px solid #DFDFE4;
    padding-top: 24px;
    @include media(mobile,tablet){
    margin-top: 10px;
    padding-top: 10px;
  }
  }
  .opration-loading {
    height: 24px;
    width: 100%;
    .zm-loading-spinner {
      margin-top: -12px;
      height: 24px;
      .circular {
        width: 24px;
        height: 24px;
      }
    }
  }
  .zm-form-item {

    &__label{
      @include media(mobile,tablet){
        text-align: left;
      }
    }
    .number-item{
      @include media(mobile,tablet){
        margin: 10px 0;
        .form-row{
        flex-direction: row;
        }
      }
    }
    .form-row {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;
      @include media(mobile,tablet){
        &.form-row-1{
          flex-direction: column;
          align-items: flex-start;
        }
      }
      .edit-numbers {
        font-weight: 600 !important;
      }
    }
    .label-title-1 {
      font-family: 'Almaden Sans';
      font-style: normal;
      font-weight: 700;
      font-size: 18px;
      line-height: 150%;
      color: #00031F;
      @include media(mobile,tablet){
      margin-bottom: 0;
      }
    }

    .label-title-2 {
      font-family: 'Almaden Sans';
      font-style: normal;
      font-weight: 600;
      font-size: 16px;
      line-height: 150%;
      color: #00031F;
    }
    .label-title-sub {
      font-family: 'Almaden Sans';
      font-style: normal;
      font-weight: 450;
      font-size: 12px;
      line-height: 120%;
      color: #666487;
      margin-top: 2px;
    }

    &.horizontal-form-item {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      .zm-form-item__header {
        flex: 1;
      }
      .zm-form-item__content {
        flex-shrink: 0;
      }
    }
    &.vertical-form-item {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      .zm-form-item__header {
        width: 100%;
        margin-bottom: 8px;
        @include media(mobile,tablet){
          display: flex;
        }
      }
      .zm-form-item__content {
        width: 100%;
        .opc-card-radio {
          width: 100%;
          display: flex;
          flex-wrap: wrap;
          &.billing-cycle-radios {
            .zm-radio__label--inner {
              padding: 16px;
            }
          }
          &.calling-fees-radios {
            .zm-radio__label--inner {
              padding: 16px;
            }
          }
          .zm-radio {
            flex: 1;
            .zm-radio__wrap {
              width: 100%;
            }
            .zm-radio__label--inner {
              width: 100%;
            }
          }
        }
      }
    }
    .zm-form-item__label {
      font-family: 'Almaden Sans';
      font-style: normal;
      font-weight: 700;
      font-size: 18px;
      line-height: 150%;
      color: #00031F;
      padding: 0;
    }
  }

  &__cycle-title {
    color: #00053D;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;
  }
  &__cycle-cost {
    font-family: 'Almaden Sans';
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    letter-spacing: 0em;
    color: #515079;
    &-price {
      color: #00053D;
      font-size: 22px;
      font-style: normal;
      font-weight: 700;
      line-height: 33px;
    }
  }
  &__fees-title {
    font-family: 'Almaden Sans';
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 150%;
    color: #00031F;
  }
  &__fees-cost {
    font-family: 'Almaden Sans';
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    letter-spacing: 0em;
    color: #666487;
    text-align: left;
    &-price {
      font-family: 'Almaden Sans';
      font-size: 22px;
      font-weight: 700;
      line-height: 33px;
      letter-spacing: 0em;
      text-align: left;
      color: #00031F;
    }
  }
  .main-color-icon {
    color: $theme-color;
  }
  .toll-free-label {
    font-family: 'Almaden Sans';
    font-style: normal;
    font-weight: 500;
    font-size: 12px;
    line-height: 120%;
    color: $theme-color;
    padding: 1px 4px;
    background: rgba(0, 5, 61, 0.08);
    border-radius: 4px;
    margin-left: 8px;
  }
  .zm-tag--middle.number-tag {
    padding: 8px 10px;
    margin-right: 8px;
    margin-bottom: 8px;
    .zm-tag__label {
      padding: 0;
      font-family: 'Almaden Sans';
      font-style: normal;
      font-weight: 500;
      font-size: 14px;
      line-height: 120%;
      color: #00053D;
    }
    .zm-tag__close.zm-button{
      transform: scale(1.5);

      &:hover {
        background-color: transparent !important;
      }
    }
  }

  .billing-icon-warning {
    width: 16px;
    height: 16px;
    position: relative;
    &::before {
      content: '';
      background-image: url('../../../assets/Alert.svg');
      width: 16px;
      height: 16px;
      background-repeat: no-repeat;
      background-size: cover;
      position: absolute;
      top: 0;
      left: 0;
    }
  }
  .zm-alert {
    margin-bottom: 8px;
    font-family: 'Almaden Sans';
    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 150%;
    .number-tips-info {
      display: flex;
      align-items: center;
      p {
        margin-left: 13px;
      }
    }
    .zm-icons.zm-icons--mini {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }
    &--warning {
      color: #815A30;
    }

    &--info {
      color: #515079;
    }
  }
  .opc-pbx__desc {
    ul, li {
      list-style: none;
    }
    li {
      font-family: 'Almaden Sans';
      font-style: normal;
      font-weight: 500;
      font-size: 14px;
      line-height: 150%;
      color: #00053D;
    }
  }
}
</style>
